import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "sp-v2" };
const _hoisted_2 = {
    key: 0,
    class: "sections-stack"
};
const _hoisted_3 = {
    key: 1,
    class: "sections-stack"
};
const _hoisted_4 = { class: "section-header-row" };
const _hoisted_5 = { class: "section-title" };
const _hoisted_6 = { class: "plan-rows-card" };
const _hoisted_7 = { class: "plan-row-left" };
const _hoisted_8 = { class: "cat-name" };
const _hoisted_9 = { class: "plan-amount" };
const _hoisted_10 = { class: "section-title" };
const _hoisted_11 = { class: "section-empty-text" };
const _hoisted_12 = { class: "detail-row" };
const _hoisted_13 = { class: "detail-info" };
const _hoisted_14 = { class: "detail-cat-name" };
const _hoisted_15 = { class: "detail-avg" };
const _hoisted_16 = { class: "amount-controls" };
const _hoisted_17 = ["onClick"];
const _hoisted_18 = ["onClick"];
const _hoisted_19 = ["src", "onClick"];
const _hoisted_20 = {
    key: 1,
    class: "detail-empty"
};
const _hoisted_21 = {
    key: 2,
    class: "addable-section"
};
const _hoisted_22 = { class: "addable-heading" };
const _hoisted_23 = { class: "addable-pills" };
const _hoisted_24 = ["onClick"];
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useSpending } from '@/composables/useSpending';
import { getTransactionClassificationTree, getSpendingAnalysis, clearSpendingAnalysisCache } from '@/composables/useSpendingAnalysis';
import HeaderBlock from './elements/HeaderBlock.vue';
import DateRangeDrawer from './elements/DateRangeDrawer.vue';
import MGButton from './elements/MGButton.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendingPlanPage',
    setup(__props) {
        const state = useAppState();
        const route = useRoute();
        useAuth();
        const { getCustomer } = useCustomer();
        const { getBankingInfo } = useBanking();
        const spending = useSpending();
        const dateDrawerOpen = ref(false);
        const dateSelection = ref(null);
        const activeSectionKey = ref(null);
        const detailDrawerOpen = ref(false);
        const classificationTree = ref(null);
        const analysis = ref(null);
        const loaded = computed(() => classificationTree.value !== null && analysis.value !== null);
        function currentMonthRange() {
            const now = new Date();
            let year = now.getUTCFullYear();
            let month = now.getUTCMonth();
            const cycleDay = state.bankInfo?.spendingCycle?.day;
            if (cycleDay && now.getUTCDate() >= cycleDay) {
                month += 1;
                if (month > 11) {
                    month = 0;
                    year += 1;
                }
            }
            return {
                from: new Date(Date.UTC(year, month, 1)),
                to: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59)),
            };
        }
        function loadAnalysis(refreshCache = false) {
            const { from, to } = currentMonthRange();
            getSpendingAnalysis(from, to, { snapToSpendingCycle: true }, (result) => {
                analysis.value = result;
            }, refreshCache);
        }
        function isLightColour(hex) {
            if (!hex)
                return true;
            const c = hex.replace('#', '');
            const full = c.length === 3 ? c.split('').map(x => x + x).join('') : c;
            const r = parseInt(full.slice(0, 2), 16);
            const g = parseInt(full.slice(2, 4), 16);
            const b = parseInt(full.slice(4, 6), 16);
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return luminance > 0.6;
        }
        const HIDDEN_PARENTS = new Set(['Income', 'General']);
        const sectionMeta = computed(() => {
            const parents = classificationTree.value?.parents ?? [];
            return parents.filter(p => !HIDDEN_PARENTS.has(p.name)).map(parent => {
                const bg = parent.color ?? '#E5DEDD';
                const light = isLightColour(bg);
                return {
                    key: parent.name,
                    title: `Plan for the ${parent.name}`,
                    bgColour: bg,
                    textColour: light ? '#413655' : '#FFFFFF',
                    buttonVariant: light ? 'dark' : 'light',
                    emptyText: `You haven't added any plans for ${parent.name}.`,
                };
            });
        });
        const sectionPlans = computed(() => {
            const result = {};
            for (const meta of sectionMeta.value)
                result[meta.key] = [];
            const cats = analysis.value?.categories ?? [];
            for (const c of cats) {
                if (!c.plan)
                    continue;
                if (!(c.name in result))
                    continue;
                const cls = c.plan.mgClassification;
                result[c.name].push({
                    name: c.subCategory ?? cls?.fullName ?? '',
                    iconColour: cls?.color ?? '#AA97E6',
                    planAmount: c.plan.amount,
                    currentAmount: Math.round(c.totalOut),
                });
            }
            return result;
        });
        // --- Draft state for the detail drawer ---
        const draftCategories = ref([]);
        const pendingDeleteIds = ref([]);
        const saving = ref(false);
        const hasChanges = computed(() => pendingDeleteIds.value.length > 0
            || draftCategories.value.some(d => !d.pristine && d.planAmount !== d.originalAmount));
        const activeMeta = computed(() => {
            if (!activeSectionKey.value)
                return null;
            return sectionMeta.value.find(s => s.key === activeSectionKey.value) ?? null;
        });
        const availableToAdd = computed(() => {
            if (!activeSectionKey.value)
                return [];
            const tree = classificationTree.value;
            if (!tree?.parents)
                return [];
            const addedNames = new Set(draftCategories.value.map(c => c.name));
            const cats = analysis.value?.categories ?? [];
            const avgBySub = new Map();
            for (const c of cats) {
                if (c.subCategory)
                    avgBySub.set(c.subCategory, Math.round(c.averagePerMonth ?? 0));
            }
            const result = [];
            for (const parent of tree.parents) {
                if (parent.name !== activeSectionKey.value)
                    continue;
                for (const sub of parent.subClassifications ?? []) {
                    const displayName = sub.mgSubClassification ?? sub.fullName;
                    if (addedNames.has(displayName))
                        continue;
                    result.push({
                        name: displayName,
                        iconColour: sub.color ?? parent.color ?? '#AA97E6',
                        avgPerMonth: avgBySub.get(displayName) ?? 0,
                        classification: sub,
                    });
                }
            }
            return result.sort((a, b) => {
                if (a.name === 'Other' && b.name !== 'Other')
                    return 1;
                if (a.name !== 'Other' && b.name === 'Other')
                    return -1;
                return a.name.localeCompare(b.name);
            });
        });
        function onDateRangeSelect(selection) {
            dateSelection.value = selection;
        }
        function openDetail(sectionKey) {
            activeSectionKey.value = sectionKey;
            pendingDeleteIds.value = [];
            const cats = analysis.value?.categories ?? [];
            const planned = cats.filter(c => c.plan && c.name === sectionKey);
            draftCategories.value = planned.map(c => {
                const cls = c.plan.mgClassification;
                return {
                    name: c.subCategory ?? cls?.fullName ?? '',
                    iconColour: cls?.color ?? '#AA97E6',
                    avgPerMonth: Math.round(c.averagePerMonth ?? 0),
                    planAmount: c.plan.amount,
                    originalAmount: c.plan.amount,
                    pristine: false,
                    classification: cls,
                    existingPlanId: c.plan.id,
                };
            });
            detailDrawerOpen.value = true;
        }
        function addCategory(cat) {
            draftCategories.value.push({
                name: cat.name,
                iconColour: cat.iconColour,
                avgPerMonth: cat.avgPerMonth,
                planAmount: cat.avgPerMonth,
                originalAmount: null,
                pristine: true,
                classification: cat.classification,
                existingPlanId: null,
            });
        }
        function adjustAmount(cat, direction) {
            if (cat.pristine) {
                cat.pristine = false;
                return;
            }
            const v = cat.planAmount;
            const step = v >= 100 ? 50 : 10;
            let next;
            if (direction > 0) {
                next = Math.floor(v / step) * step + step;
            }
            else {
                next = v % step === 0 ? v - step : Math.floor(v / step) * step;
            }
            cat.planAmount = Math.max(0, next);
        }
        function removeCategory(cat) {
            if (cat.existingPlanId)
                pendingDeleteIds.value.push(cat.existingPlanId);
            const i = draftCategories.value.indexOf(cat);
            if (i >= 0)
                draftCategories.value.splice(i, 1);
        }
        function analysisReflectsChanges(result, parentName, expected, deletedIds) {
            if (!result)
                return false;
            const cats = result.categories ?? [];
            for (const id of deletedIds) {
                if (cats.some(c => c.plan?.id === id))
                    return false;
            }
            for (const e of expected) {
                const match = cats.find(c => c.name === parentName && c.subCategory === e.subCategory && c.plan && c.plan.amount === e.amount);
                if (!match)
                    return false;
            }
            return true;
        }
        let pollTimeoutId = null;
        let pollCancelled = false;
        function pollAnalysisUntilReflected(parentName, expected, deletedIds, onDone) {
            const maxAttempts = 6;
            const delayMs = 500;
            let attempt = 0;
            pollCancelled = false;
            const tryOnce = () => {
                if (pollCancelled)
                    return;
                attempt += 1;
                const { from, to } = currentMonthRange();
                getSpendingAnalysis(from, to, { snapToSpendingCycle: true }, (result) => {
                    if (pollCancelled)
                        return;
                    analysis.value = result;
                    if (analysisReflectsChanges(result, parentName, expected, deletedIds) || attempt >= maxAttempts) {
                        onDone();
                    }
                    else {
                        pollTimeoutId = setTimeout(tryOnce, delayMs);
                    }
                }, /* refreshCache */ true);
            };
            tryOnce();
        }
        function saveDraft() {
            if (saving.value)
                return;
            if (!activeSectionKey.value)
                return;
            const zeroedExisting = draftCategories.value.filter(d => d.existingPlanId && d.planAmount === 0 && !d.pristine);
            const drafts = draftCategories.value.filter(d => d.classification && d.planAmount > 0 && d.planAmount !== d.originalAmount && !d.pristine);
            const deletes = pendingDeleteIds.value.slice();
            for (const d of zeroedExisting) {
                if (d.existingPlanId && !deletes.includes(d.existingPlanId))
                    deletes.push(d.existingPlanId);
            }
            if (drafts.length === 0 && deletes.length === 0) {
                detailDrawerOpen.value = false;
                return;
            }
            const parentName = activeSectionKey.value;
            const expected = drafts.map(d => ({ subCategory: d.name, amount: d.planAmount }));
            saving.value = true;
            let remaining = drafts.length + deletes.length;
            const finish = () => {
                remaining -= 1;
                if (remaining === 0) {
                    pendingDeleteIds.value = [];
                    clearSpendingAnalysisCache();
                    pollAnalysisUntilReflected(parentName, expected, deletes, () => {
                        saving.value = false;
                        detailDrawerOpen.value = false;
                    });
                }
            };
            for (const d of drafts) {
                const c = d.classification;
                const payload = {
                    mgClassification: {
                        id: c.id,
                        key: c.key,
                        mgClassification: c.mgClassification,
                        mgSubClassification: c.mgSubClassification,
                        grandParentClassification: c.grandParentClassification,
                        iconPath: c.iconPath,
                    },
                    amount: d.planAmount,
                };
                spending.addSpendingPlan(payload, finish);
            }
            for (const id of deletes) {
                spending.deleteSpendingPlan(id, finish);
            }
        }
        let autoOpenedSection = false;
        watch(loaded, (isLoaded) => {
            if (!isLoaded || autoOpenedSection)
                return;
            const requested = route.query.section;
            if (typeof requested !== 'string')
                return;
            autoOpenedSection = true;
            const match = sectionMeta.value.find(s => s.key === requested);
            if (match)
                openDetail(match.key);
        });
        onMounted(() => {
            getCustomer(() => {
                getBankingInfo(() => {
                    loadAnalysis();
                    getTransactionClassificationTree((tree) => {
                        classificationTree.value = tree;
                    });
                });
            });
        });
        onUnmounted(() => {
            pollCancelled = true;
            if (pollTimeoutId) {
                clearTimeout(pollTimeoutId);
                pollTimeoutId = null;
            }
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, { title: "Future plans" }),
                _createElementVNode("div", _hoisted_1, [
                    _cache[5] || (_cache[5] = _createElementVNode("div", { class: "hero" }, [
                        _createElementVNode("h2", { class: "hero-title" }, "Spending plan management"),
                        _createElementVNode("p", { class: "hero-subtitle" }, "Create and maintain your spending plans, and track progress over time.")
                    ], -1)),
                    (!loaded.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (n) => {
                                return _createVNode(_component_v_skeleton_loader, {
                                    key: n,
                                    type: "image",
                                    height: "180px",
                                    class: "skeleton-section"
                                });
                            }), 64))
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sectionMeta.value, (meta) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: meta.key,
                                    class: "plan-section",
                                    style: _normalizeStyle({
                                        background: meta.bgColour,
                                        color: meta.textColour,
                                    })
                                }, [
                                    (sectionPlans.value[meta.key].length > 0)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createElementVNode("div", _hoisted_4, [
                                                _createElementVNode("span", _hoisted_5, _toDisplayString(meta.title), 1),
                                                _cache[3] || (_cache[3] = _createElementVNode("span", { class: "col-label" }, "Plan", -1)),
                                                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "col-label" }, "Current", -1))
                                            ]),
                                            _createElementVNode("div", _hoisted_6, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sectionPlans.value[meta.key], (cat) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        key: cat.name,
                                                        class: "plan-row"
                                                    }, [
                                                        _createElementVNode("div", _hoisted_7, [
                                                            _createElementVNode("div", {
                                                                class: "cat-dot",
                                                                style: _normalizeStyle({ background: cat.iconColour })
                                                            }, null, 4),
                                                            _createElementVNode("span", _hoisted_8, _toDisplayString(cat.name), 1)
                                                        ]),
                                                        _createElementVNode("span", _hoisted_9, "£" + _toDisplayString(cat.planAmount), 1),
                                                        _createElementVNode("span", {
                                                            class: _normalizeClass(["current-amount", { 'current-amount-over': cat.currentAmount > cat.planAmount }])
                                                        }, "£" + _toDisplayString(cat.currentAmount), 3)
                                                    ]));
                                                }), 128))
                                            ]),
                                            _createVNode(MGButton, {
                                                outline: true,
                                                transparent: true,
                                                arrow: true,
                                                noCaps: true,
                                                leftAlign: true,
                                                text: "Edit / add new",
                                                onClick: ($event) => (openDetail(meta.key))
                                            }, null, 8, ["onClick"])
                                        ], 64))
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            _createElementVNode("span", _hoisted_10, _toDisplayString(meta.title), 1),
                                            _createElementVNode("p", _hoisted_11, _toDisplayString(meta.emptyText), 1),
                                            _createElementVNode("div", {
                                                class: _normalizeClass({ 'light-button': meta.buttonVariant === 'light' })
                                            }, [
                                                _createVNode(MGButton, {
                                                    outline: true,
                                                    transparent: true,
                                                    arrow: true,
                                                    noCaps: true,
                                                    leftAlign: true,
                                                    small: true,
                                                    text: "Create new plan",
                                                    onClick: ($event) => (openDetail(meta.key))
                                                }, null, 8, ["onClick"])
                                            ], 2)
                                        ], 64))
                                ], 4));
                            }), 128))
                        ]))
                ]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: detailDrawerOpen.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((detailDrawerOpen).value = $event)),
                    title: activeMeta.value?.title ?? 'Plans',
                    height: "85vh"
                }, {
                    footer: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: saving.value ? 'Updating…' : (hasChanges.value ? 'Update' : 'Close'),
                            fullWidth: true,
                            disabled: saving.value,
                            onClick: _cache[0] || (_cache[0] = ($event) => (hasChanges.value ? saveDraft() : (detailDrawerOpen.value = false)))
                        }, null, 8, ["text", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        (draftCategories.value.length > 0)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _cache[8] || (_cache[8] = _createElementVNode("p", { class: "detail-heading" }, "Set your monthly plans below", -1)),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(draftCategories.value, (cat) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: cat.name,
                                        class: "detail-row-wrap"
                                    }, [
                                        _createElementVNode("div", _hoisted_12, [
                                            _createElementVNode("div", {
                                                class: "detail-dot",
                                                style: _normalizeStyle({ background: cat.iconColour })
                                            }, null, 4),
                                            _createElementVNode("div", _hoisted_13, [
                                                _createElementVNode("span", _hoisted_14, _toDisplayString(cat.name), 1),
                                                _createElementVNode("span", _hoisted_15, "£" + _toDisplayString(cat.avgPerMonth) + " avg. /month", 1)
                                            ]),
                                            _createElementVNode("div", _hoisted_16, [
                                                _createElementVNode("div", {
                                                    class: "amount-btn",
                                                    onClick: ($event) => (adjustAmount(cat, -1))
                                                }, _cache[6] || (_cache[6] = [
                                                    _createElementVNode("span", { class: "amount-btn-icon" }, "−", -1)
                                                ]), 8, _hoisted_17),
                                                _createElementVNode("span", {
                                                    class: _normalizeClass(["detail-plan-amount", { 'detail-plan-amount-pristine': cat.pristine }])
                                                }, "£" + _toDisplayString(cat.planAmount), 3),
                                                _createElementVNode("div", {
                                                    class: "amount-btn",
                                                    onClick: ($event) => (adjustAmount(cat, 1))
                                                }, _cache[7] || (_cache[7] = [
                                                    _createElementVNode("span", { class: "amount-btn-icon" }, "+", -1)
                                                ]), 8, _hoisted_18)
                                            ])
                                        ]),
                                        (!cat.pristine)
                                            ? (_openBlock(), _createElementBlock("img", {
                                                key: 0,
                                                src: require('@/assets/trashcan.png'),
                                                class: "detail-trash",
                                                alt: "Remove",
                                                onClick: ($event) => (removeCategory(cat))
                                            }, null, 8, _hoisted_19))
                                            : _createCommentVNode("", true)
                                    ]));
                                }), 128))
                            ], 64))
                            : (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[9] || (_cache[9] = [
                                _createElementVNode("p", { class: "detail-empty-text" }, "Tap a category below to add it to your plan", -1)
                            ]))),
                        (availableToAdd.value.length > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                _createElementVNode("p", _hoisted_22, _toDisplayString(draftCategories.value.length > 0 ? 'Add another category' : 'Choose categories'), 1),
                                _createElementVNode("div", _hoisted_23, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableToAdd.value, (cat) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                            key: cat.name,
                                            class: "addable-pill",
                                            onClick: ($event) => (addCategory(cat))
                                        }, [
                                            _createElementVNode("span", {
                                                class: "addable-badge",
                                                style: _normalizeStyle({ background: cat.iconColour })
                                            }, [
                                                _createVNode(_component_v_icon, {
                                                    size: "10",
                                                    color: "white"
                                                }, {
                                                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                        _createTextVNode("mdi-plus")
                                                    ])),
                                                    _: 1
                                                })
                                            ], 4),
                                            _createElementVNode("span", null, _toDisplayString(cat.name), 1)
                                        ], 8, _hoisted_24));
                                    }), 128))
                                ])
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                _createVNode(DateRangeDrawer, {
                    modelValue: dateDrawerOpen.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((dateDrawerOpen).value = $event)),
                    initialSelection: dateSelection.value,
                    onSelect: onDateRangeSelect
                }, null, 8, ["modelValue", "initialSelection"])
            ], 64));
        };
    }
});
